/**
 * Aplicación de `BoardMod` (efectos de cartas sobre el tablero) a un
 * `BoardDef` concreto. Lógica pura: recibe el tablero elegido para el
 * vertido más los mods activos de la run y devuelve una COPIA nueva del
 * tablero ya modificada — nunca muta el `BoardDef` original (que suele
 * venir directo de `boards.json` y se reutiliza entre runs).
 */

import type { BarDef, BoardDef, BoardMod } from '../../core/types';
import type { Rng } from '../../core/rng';
import { MULT_MAX, MYSTERY_MAX, MYSTERY_MIN } from './constants';

interface FlatBarRef {
  rowIndex: number;
  barIndex: number;
  bar: BarDef;
}

function flattenBars(board: BoardDef): FlatBarRef[] {
  const refs: FlatBarRef[] = [];
  board.rows.forEach((row, rowIndex) => {
    row.bars.forEach((bar, barIndex) => refs.push({ rowIndex, barIndex, bar }));
  });
  return refs;
}

function cloneBoard(board: BoardDef): BoardDef {
  return {
    id: board.id,
    rows: board.rows.map((row) => ({
      y: row.y,
      bars: row.bars.map((bar) => ({ ...bar })),
    })),
  };
}

/**
 * Aplica, en orden, los `BoardMod` otorgados por las cartas del run al
 * tablero elegido para el próximo vertido. Reglas:
 *
 *  - `extraMultipliers`: sube `value` a hasta `count` barras 'mult'
 *    elegidas al azar (tope `MULT_MAX` para no romper el balance). Si hay
 *    menos barras 'mult' que `count`, se suben todas las que haya.
 *  - `extraTrampoline`: convierte una barra 'mult' al azar en 'trampoline'
 *    (le quita el valor numérico, ya no aplica). No-op si no hay barras
 *    'mult' disponibles.
 *  - `revealMystery`: revela de antemano (fija `value`) todas las barras
 *    'mystery' que aún no tuvieran un valor asignado; las que ya lo tenían
 *    (p.ej. por otra carta) quedan intactas.
 */
export function applyBoardMods(board: BoardDef, mods: readonly BoardMod[], rng: Rng): BoardDef {
  const result = cloneBoard(board);

  for (const mod of mods) {
    if (mod.kind === 'extraMultipliers') {
      applyExtraMultipliers(result, mod.count, mod.value, rng);
    } else if (mod.kind === 'extraTrampoline') {
      applyExtraTrampoline(result, rng);
    } else if (mod.kind === 'revealMystery') {
      applyRevealMystery(result, rng);
    }
  }

  return result;
}

function applyExtraMultipliers(board: BoardDef, count: number, value: number, rng: Rng): void {
  const multBars = flattenBars(board).filter((ref) => ref.bar.kind === 'mult');
  const targets = rng.shuffle(multBars).slice(0, Math.max(0, count));
  for (const ref of targets) {
    const current = ref.bar.value ?? 0;
    ref.bar.value = Math.min(MULT_MAX, current + value);
  }
}

function applyExtraTrampoline(board: BoardDef, rng: Rng): void {
  const multBars = flattenBars(board).filter((ref) => ref.bar.kind === 'mult');
  if (multBars.length === 0) return;
  const chosen = rng.pick(multBars);
  chosen.bar.kind = 'trampoline';
  delete chosen.bar.value;
}

function applyRevealMystery(board: BoardDef, rng: Rng): void {
  for (const ref of flattenBars(board)) {
    if (ref.bar.kind === 'mystery' && ref.bar.value === undefined) {
      ref.bar.value = rng.int(MYSTERY_MIN, MYSTERY_MAX);
    }
  }
}
